import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Loader } from '@/components/custom/loader';
import { useSession } from '@/hooks/useSession';
import { useClient } from 'jsonapi-react';
import React, { useEffect, useRef, useState } from 'react';
import { useMemo, memo } from 'react';
import { ExamsQuestionsContent } from './exams-questions-content';
import { ExamsQuestionsSidebar } from './exams-questions-sidebar';
import { Error404 } from '@/components/custom/error';
import { i18n } from '@/lib/i18n/i18n';
import { Dashboard } from '@/layouts/dashboard';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';

export const ExamsQuestions = memo(function ExamsQuestions() {
  const client = useClient();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useSession();
  const playfulStudent = usePlayfulStudent();
  const [isLoading, setIsLoading] = useState(true);
  const [question, setQuestion] = useState();
  const [currentQuestion, setCurrentQuestion] = useState();
  const [questions, setQuestions] = useState([]);
  const [taxonomies, setTaxonomies] = useState([]);
  const [questionBook, setQuestionBook] = useState({});
  const [questionBookUser, setQuestionBookUser] = useState();
  const [timeLeft, setTimeLeft] = useState(null);
  const [progressPercent, setProgressPercent] = useState(0);
  const [activeGroupedQuestions, setActiveGroupedQuestions] = useState(0);
  const [notAllowed, setNotAllowed] = useState(false);
  const [questionBooksUsers, setQuestionBooksUsers] = useState([]);
  const reference = useRef(null);

  /**
   * Lógica para determinar o destino do redirecionamento baseada na URL.
   * Se estiver em /provas/:id -> /avaliacoes
   * Se estiver em /simulados/:id -> /simulados
   */
  const finishRedirectPath = useMemo(() => {
    if (location.pathname.includes('/provas/')) {
      return '/avaliacoes';
    }
    if (location.pathname.includes('/simulados/')) {
      return '/simulados';
    }
    return '/questions/finish';
  }, [location.pathname]);

  useEffect(() => {
    i18n.changeLanguage(process.env.REACT_APP_LOCALE);
  }, []);

  useEffect(() => {
    clearTimeout(reference.current);
    reference.current = setTimeout(getQuestionBook, 500);
    return () => clearTimeout(reference.current);
  }, [questionBooksUsers]);

  const examTime = parseInt(questionBook?.['resolution-time'], 10);

  const isBetweenOpenDays = questionBook => {
    return true;
  };

  const groupQuestionsByCode = items => {
    if (items?.length > 0) {
      return items.reduce((result, item) => {
        (result[item.code] = result[item.code] || []).push(item);
        return result;
      }, {});
    }
  };

  const selectQuestionBookTaxonomy = newIndex => {
    setActiveGroupedQuestions(newIndex);
  };

  const getQuestionBook = async () => {
    setIsLoading(true);

    const { data, error } = await client.fetch(`/questions?filter[question_book_id]=${id}`);
    if (error) {
      setNotAllowed(true);
    } else {
      setQuestions(data);

      const filterQuestionBook = {
        current_user: session.user.id
      };

      const questionBookResponse = await client.fetch(['question_books', id, filterQuestionBook]);

      const examTime = parseInt(questionBookResponse.data?.['resolution-time'], 10);
      setTimeLeft(examTime * 60 * 60);

      setQuestionBook(questionBookResponse.data);

      if (isBetweenOpenDays(questionBookResponse.data)) {
        const hasLastAttempt = !!questionBookResponse.data['last-question-books-user-id'];
        const isDone = questionBookResponse.data['is-question-book-done'];
        const canRetry = questionBookResponse.data?.['able-to-retry'];
        const hasLearningSystem = !!questionBookResponse.data['learning-system-ids']?.[0];

        if (!hasLastAttempt) {
          // primeira tentativa
          const response = await client.mutate('question_books_users', {
            question_book_id: id
          });

          setQuestionBookUser(response);
        } else if (isDone && canRetry) {
          // refazer prova -> NOVA tentativa limpa
          const response = await client.mutate('question_books_users', {
            question_book_id: id
          });

          setQuestionBookUser(response);

          // limpa respostas locais
          questionBookResponse.data.taxonomies = questionBookResponse.data.taxonomies.map(taxonomy => ({
            ...taxonomy,
            answers: []
          }));
        } else if (isDone && hasLearningSystem) {
          const response = await client.mutate('question_books_users', {
            question_book_id: id
          });

          setQuestionBookUser(response);

          questionBookResponse.data.taxonomies = questionBookResponse.data.taxonomies.map(taxonomy => ({
            ...taxonomy,
            answers: []
          }));
        } else if (isDone && !canRetry) {
          navigate(finishRedirectPath);
        } else {
          // tentativa em andamento → continua a mesma
          const response = await client.fetch(['question_books_users', questionBookResponse.data['last-question-books-user-id']]);

          setQuestionBookUser(response);
        }

        const questionId = questionBookResponse.data['question-book-taxonomies']?.[0]?.question_ids?.[0];
        setCurrentQuestion(questionId);
        setQuestion(data.find(q => q.id === questionId));

        let questionTaxonomies = questionBookResponse.data.taxonomies;
        questionTaxonomies.forEach(questionTaxonomy => {
          const questionIds = questionBookResponse.data['question-book-taxonomies'].find(e => e.taxonomy_id === questionTaxonomy.id).question_ids;
          const tQuestions = data.filter(q => questionIds.includes(q.id));
          questionTaxonomy.selected = false;
          questionTaxonomy.questions = tQuestions;
          questionTaxonomy.groupedQuestions = groupQuestionsByCode(tQuestions);
        });

        if (questionTaxonomies[0]) {
          questionTaxonomies[0].selected = true;
        }
        setTaxonomies(questionTaxonomies);
      } else {
        window.location.pathname = '/open-onboarding';
      }

      setIsLoading(false);
    }
  };

  const updateLastAnswerSubmited = answer => {
    let questionIndex;
    const taxonomyIndex = taxonomies.findIndex(taxonomy => {
      questionIndex = taxonomy.questions.findIndex(question => question.id === answer.questionId);
      return questionIndex > -1;
    });

    let newTaxonomies = taxonomies;
    const answerIndex = newTaxonomies[taxonomyIndex]?.answers.findIndex(ans => ans.id === answer.id);

    if (answerIndex > -1) {
      newTaxonomies[taxonomyIndex].answers[answerIndex] = answer;
    } else if (taxonomyIndex > -1) {
      newTaxonomies[taxonomyIndex].answers.push(answer);
    }

    setTaxonomies([...newTaxonomies]);
  };

  useEffect(() => {
    setProgressPercent((currentQuestion * 100) / questions?.length);
  }, [currentQuestion, questions?.length]);

  const filteredQuestions = useMemo(() => taxonomies.map(taxonomy => questions.filter(question => question.taxonomies.some(questionTaxonomy => questionTaxonomy?.id === taxonomy.id))), [taxonomies, questions]);
  const taxonomyNames = useMemo(
    () =>
      questionBook?.['question-book-taxonomies']?.map(qbTaxonomy => {
        const taxonomy = taxonomies.find(taxonomy => taxonomy.id === qbTaxonomy.taxonomy_id);
        return taxonomy?.name;
      }),
    [questionBook, taxonomies]
  );

  return !notAllowed ? (
    isLoading ? (
      <Loader />
    ) : (
      <Dashboard>
        {/* No tema lúdico a navegação de questões vive dentro do conteúdo;
            a sidebar fixa de 260px sobreporia o card da questão (ml-0). */}
        {!playfulStudent && (
          <ExamsQuestionsSidebar
            currentQuestion={currentQuestion}
            setCurrentQuestion={setCurrentQuestion}
            taxonomies={taxonomies}
            exam={questionBook}
            filteredQuestions={filteredQuestions}
            taxonomyNames={taxonomyNames}
            selectQuestionBookTaxonomy={selectQuestionBookTaxonomy}
          />
        )}

        <ExamsQuestionsContent
          selectQuestionBookTaxonomy={selectQuestionBookTaxonomy}
          updateLastAnswerSubmited={updateLastAnswerSubmited}
          currentQuestion={currentQuestion}
          setCurrentQuestion={setCurrentQuestion}
          questions={questions}
          question={question}
          examTime={examTime}
          progressPercent={progressPercent}
          questionBooksUser={questionBookUser}
          questionBook={questionBook}
          taxonomies={taxonomies}
          activeGroupedQuestions={activeGroupedQuestions}
        />
      </Dashboard>
    )
  ) : (
    <Error404 />
  );
});
